import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FavoriteButton = ({ providerId, size = 'default', showCount = false }) => {
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoriteCount, setFavoriteCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const isProvider = user.role === 'provider';

  // Size variants
  const sizeClasses = {
    small: 'w-8 h-8',
    default: 'w-10 h-10',
    large: 'w-12 h-12'
  };

  const iconSizes = {
    small: 16,
    default: 20,
    large: 24
  };

  // Check if already favorited on mount
  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (!token) {
        setChecking(false);
        return;
      }

      try {
        const response = await axios.get(
          `${API}/favorites/check/${providerId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setIsFavorited(response.data.is_favorited);
      } catch (error) {
        console.error('Error checking favorite status:', error);
      } finally {
        setChecking(false);
      }
    };

    checkFavoriteStatus();
  }, [providerId, token]);

  const handleToggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!token) {
      toast.error('Bitte melde dich an, um Favoriten zu nutzen', {
        action: {
          label: 'Anmelden',
          onClick: () => window.location.href = '/login'
        }
      });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/favorites/toggle`,
        { provider_id: providerId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setIsFavorited(response.data.is_favorited);
      setFavoriteCount(response.data.favorite_count);

      if (response.data.is_favorited) {
        toast.success('Zu Favoriten hinzugefügt');
      } else {
        toast.success('Aus Favoriten entfernt');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Fehler beim Speichern des Favoriten');
    } finally {
      setLoading(false);
    }
  };

  // Providers cannot use favorites
  if (isProvider) {
    return null;
  }

  if (checking) {
    return (
      <div className={`${sizeClasses[size]} flex items-center justify-center`}>
        <div className="animate-pulse">
          <Heart size={iconSizes[size]} className="text-gray-300" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={handleToggleFavorite}
        disabled={loading}
        className={`
          ${sizeClasses[size]} 
          flex items-center justify-center 
          rounded-full
          transition-all duration-200
          ${isFavorited 
            ? 'bg-red-50 hover:bg-red-100 text-red-500' 
            : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
          }
          ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110'}
        `}
        title={isFavorited ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
        aria-label={isFavorited ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
      >
        <Heart
          size={iconSizes[size]}
          fill={isFavorited ? 'currentColor' : 'none'}
          className="transition-all duration-200"
        />
      </button>

      {showCount && favoriteCount > 0 && (
        <span className="text-sm text-gray-600">
          {favoriteCount} {favoriteCount === 1 ? 'Favorit' : 'Favoriten'}
        </span>
      )}
    </div>
  );
};

export default FavoriteButton;
